import { isAxiosError, AxiosResponse, AxiosError } from "axios";
import { reportApiError } from "../../../../sentry";

export const APIProviderFactory: IAPIProviderFactory = ({
  API,
  apiName,
  analyticsController,
}) => {
  const GET = async (
    api: string,
    queryStringParameters = {},
    headers = {},
    signal?: AbortSignal
  ) => {
    try {
      const apiInstance: AxiosResponse = await API.get(apiName, api, {
        headers,
        queryStringParameters,
        signal,
        response: true,
      });
      return apiInstance.data;
    } catch (error: unknown) {
      const axiosError = error as AxiosError;
      const isAxios = isAxiosError(axiosError);

      // Report all API errors to Sentry (not just authenticated ones)
      const status = isAxios ? axiosError.response?.status : undefined;
      const url = isAxios ? axiosError.config?.url : undefined;

      // Report errors with status codes (except 404) OR network errors (CORS, timeout, etc.)
      if ((status && status !== 404) || (!status && isAxios)) {
        reportApiError(
          new Error(`API GET failed: ${axiosError.message || "Unknown error"}`),
          {
            method: "GET",
            url,
            status,
            requestBody: queryStringParameters,
            responseBody: isAxios ? axiosError.response?.data : undefined,
          }
        );
      }

      if (isAxios) {
        return axiosError.response?.data;
      }
      throw new Error("Something went wrong!");
    }
  };

  const POST = async (api: string, body = {}, headers = {}) => {
    try {
      const apiInstance: AxiosResponse = await API.post(apiName, api, {
        headers,
        body,
        response: true,
      });
      return apiInstance.data;
    } catch (error: unknown) {
      const axiosError = error as AxiosError;
      const isAxios = isAxiosError(axiosError);

      // Report all API errors to Sentry
      const status = isAxios ? axiosError.response?.status : undefined;
      const url = isAxios ? axiosError.config?.url : undefined;

      // Report errors with status codes (except 404) OR network errors (CORS, timeout, etc.)
      if ((status && status !== 404) || (!status && isAxios)) {
        reportApiError(
          new Error(
            `API POST failed: ${axiosError.message || "Unknown error"}`
          ),
          {
            method: "POST",
            url,
            status,
            requestBody: body,
            responseBody: isAxios ? axiosError.response?.data : undefined,
          }
        );
      }

      if (isAxios) {
        return axiosError.response?.data;
      }
      throw new Error("Something went wrong!");
    }
  };

  const PUT = async (api: string, body = {}, headers = {}) => {
    try {
      const apiInstance: AxiosResponse = await API.put(apiName, api, {
        headers,
        body,
        response: true,
      });
      return apiInstance.data;
    } catch (error: unknown) {
      const axiosError = error as AxiosError;
      const isAxios = isAxiosError(axiosError);

      // Report all API errors to Sentry
      const status = isAxios ? axiosError.response?.status : undefined;
      const url = isAxios ? axiosError.config?.url : undefined;

      // Report errors with status codes (except 404) OR network errors (CORS, timeout, etc.)
      if ((status && status !== 404) || (!status && isAxios)) {
        reportApiError(
          new Error(`API PUT failed: ${axiosError.message || "Unknown error"}`),
          {
            method: "PUT",
            url,
            status,
            requestBody: body,
            responseBody: isAxios ? axiosError.response?.data : undefined,
          }
        );
      }

      if (isAxios) {
        return axiosError.response?.data;
      }
      throw new Error("Something went wrong!");
    }
  };

  const PATCH = async (api: string, body = {}, headers = {}) => {
    try {
      const apiInstance: AxiosResponse = await API.patch(apiName, api, {
        headers,
        body,
        response: true,
      });
      return apiInstance.data;
    } catch (error: unknown) {
      const axiosError = error as AxiosError;
      const isAxios = isAxiosError(axiosError);

      // Report all API errors to Sentry
      const status = isAxios ? axiosError.response?.status : undefined;
      const url = isAxios ? axiosError.config?.url : undefined;

      // Report errors with status codes (except 404) OR network errors (CORS, timeout, etc.)
      if ((status && status !== 404) || (!status && isAxios)) {
        reportApiError(
          new Error(
            `API PATCH failed: ${axiosError.message || "Unknown error"}`
          ),
          {
            method: "PATCH",
            url,
            status,
            requestBody: body,
            responseBody: isAxios ? axiosError.response?.data : undefined,
          }
        );
      }

      if (isAxios) {
        return axiosError.response?.data;
      }
      throw new Error("Something went wrong!");
    }
  };

  const DELETE = async (api: string, body = {}, headers = {}) => {
    try {
      const apiInstance: AxiosResponse = await API.del(apiName, api, {
        headers,
        body,
        response: true,
      });
      return apiInstance.data;
    } catch (error: unknown) {
      const axiosError = error as AxiosError;
      const isAxios = isAxiosError(axiosError);

      // Report all API errors to Sentry
      const status = isAxios ? axiosError.response?.status : undefined;
      const url = isAxios ? axiosError.config?.url : undefined;

      // Report errors with status codes (except 404) OR network errors (CORS, timeout, etc.)
      if ((status && status !== 404) || (!status && isAxios)) {
        reportApiError(
          new Error(
            `API DELETE failed: ${axiosError.message || "Unknown error"}`
          ),
          {
            method: "DELETE",
            url,
            status,
            requestBody: body,
            responseBody: isAxios ? axiosError.response?.data : undefined,
          }
        );
      }

      if (isAxios) {
        return axiosError.response?.data;
      }
      throw new Error("Something went wrong!");
    }
  };

  return {
    GET,
    POST,
    PUT,
    PATCH,
    DELETE,
  };
};
